import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Scroll from 'react-scroll';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilterCircleXmark, faFilter, faArrowLeft, faTasks, faTachometer, faEye, faPencil, faTrashCan, faPlus, faTable, faArrowRight, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { useRecoilState } from 'recoil';

import { getSessionTemplateListAPI, deleteSessionTemplateAPI } from "../../API/SessionTemplate";
import FormErrorBox from "../Reusable/FormErrorBox";
import FormSelectField from "../Reusable/FormSelectField";
import PageLoadingContent from "../Reusable/PageLoadingContent";
import { topAlertMessageState, topAlertStatusState } from "../../AppState";
import { PAGE_SIZE_OPTIONS, SESSION_TEMPLATE_SORT_OPTIONS, SESSION_TEMPLATE_SESSION_TYPE_WITH_EMPTY_OPTIONS } from "../../Constants/FieldOptions";
import SessionTemplateListDesktop from "./ListDesktop";
import SessionTemplateListMobile from "./ListMobile";
import {
    sessionTemplatesShowFilterState, sessionTemplatesSessionTypeState, sessionTemplatesSortedByState
} from "../../AppState";


function SessionTemplateList() {

    ////
    //// Global state.
    ////

    const [topAlertMessage, setTopAlertMessage] = useRecoilState(topAlertMessageState);
    const [topAlertStatus, setTopAlertStatus] = useRecoilState(topAlertStatusState);
    const [showFilter, setShowFilter] = useRecoilState(sessionTemplatesShowFilterState);
    const [sessionType, setSessionType] = useRecoilState(sessionTemplatesSessionTypeState);  // Filtering
    const [sortByValue, setSortByValue] = useRecoilState(sessionTemplatesSortedByState);     // Sorting

    ////
    //// Component states.
    ////

    const [errors, setErrors] = useState({});
    const [selectedSessionTemplateForDeletion, setSelectedSessionTemplateForDeletion] = useState("");
    const [isFetching, setFetching] = useState(false);

    const [listData, setListData] = useState("");                // List Data + Pagination
    const [pageSize, setPageSize] = useState(10);                // Pagination
    const [previousCursors, setPreviousCursors] = useState([]);  // Pagination
    const [nextCursor, setNextCursor] = useState("");            // Pagination
    const [currentCursor, setCurrentCursor] = useState("");      // Pagination

    ////
    //// API.
    ////

    function onSessionTemplateListSuccess(response){
        console.log("onSessionTemplateListSuccess: Starting...");
        setListData(response);
        if (response.hasNextPage) {
            setNextCursor(response.nextCursor); // For pagination purposes.
        }
    }

    function onSessionTemplateListError(apiErr) {
        console.log("onSessionTemplateListError: Starting...");
        setErrors(apiErr);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onSessionTemplateListDone() {
        console.log("onSessionTemplateListDone: Starting...");
        setFetching(false);
    }

    function onSessionTemplateDeleteSuccess(response){
        console.log("onSessionTemplateDeleteSuccess: Starting..."); // For debugging purposes only.

        // Update notification.
        setTopAlertStatus("success");
        setTopAlertMessage("SessionTemplate deleted");
        setTimeout(() => {
            console.log("onDeleteConfirmButtonClick: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // Fetch again an updated list.
        defaultFetchList();
    }

    function onSessionTemplateDeleteError(apiErr) {
        console.log("onSessionTemplateDeleteError: Starting..."); // For debugging purposes only.
        setErrors(apiErr);

        // Update notification.
        setTopAlertStatus("danger");
        setTopAlertMessage("Failed deleting");
        setTimeout(() => {
            console.log("onSessionTemplateDeleteError: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onSessionTemplateDeleteDone() {
        console.log("onSessionTemplateDeleteDone: Starting...");
        setFetching(false);
    }

    ////
    //// Event handling.
    ////

    const onClearFilterClick = (e) => {
        setShowFilter(false);
        setSessionType("");
        setSortByValue("created,DESC");
    }

    // Convienience function.
    const defaultFetchList = () => {
        fetchList(currentCursor, pageSize, sortByValue, sessionType);
    }

    const fetchList = (cur, limit, sbv, st) => {
        setFetching(true);
        setErrors({});

        let params = new Map();
        params.set("page_size", limit); // Pagination

        // DEVELOPERS NOTE: Our `sortByValue` is string with the sort field
        // and sort order combined with a comma seperation. Therefore we
        // need to split as follows.
        if (sbv !== undefined && sbv !== null && sbv !== "") {
            const sortArray = sbv.split(",");
            params.set("sort_field", sortArray[0]); // Sort (1 of 2)
            params.set("sort_order", sortArray[1]); // Sort (2 of 2)
        }

        if (cur !== "") {
            params.set("cursor", cur); // Pagination
        }

        if (st !== "") {
            params.set("session_type", st); // Pagination
        }

        getSessionTemplateListAPI(
            params,
            onSessionTemplateListSuccess,
            onSessionTemplateListError,
            onSessionTemplateListDone
        );
    }

    const onNextClicked = (e) => {
        console.log("onNextClicked");
        let arr = [...previousCursors];
        arr.push(currentCursor);
        setPreviousCursors(arr);
        setCurrentCursor(nextCursor);
    }

    const onPreviousClicked = (e) => {
        console.log("onPreviousClicked");
        let arr = [...previousCursors];
        const previousCursor = arr.pop();
        setPreviousCursors(arr);
        setCurrentCursor(previousCursor);
    }

    const onSelectSessionTemplateForDeletion = (e, sessionTemplate) => {
        console.log("onSelectSessionTemplateForDeletion", sessionTemplate);
        setSelectedSessionTemplateForDeletion(sessionTemplate);
    }

    const onDeselectSessionTemplateForDeletion = (e) => {
        console.log("onDeselectSessionTemplateForDeletion");
        setSelectedSessionTemplateForDeletion("");
    }

    const onDeleteConfirmButtonClick = (e) => {
        console.log("onDeleteConfirmButtonClick"); // For debugging purposes only.

        deleteSessionTemplateAPI(
            selectedSessionTemplateForDeletion.id,
            onSessionTemplateDeleteSuccess,
            onSessionTemplateDeleteError,
            onSessionTemplateDeleteDone
        );
        setSelectedSessionTemplateForDeletion("");
    }

    ////
    //// Misc.
    ////

    useEffect(() => {
        let mounted = true;

        if (mounted) {
            window.scrollTo(0, 0);  // Start the page at the top of the page.
            fetchList(currentCursor, pageSize, sortByValue, sessionType);
        }

        return () => { mounted = false; }
    }, [currentCursor, pageSize, sortByValue, sessionType]);

    ////
    //// Component rendering.
    ////

    return (
        <>
            <div class="container">
                <section class="section">
                    {/* Desktop Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
                        <ul>
                            <li><Link to="/dashboard"><FontAwesomeIcon className="fas" icon={faTachometer} />&nbsp;Dashboard</Link></li>
                            <li class="is-active"><Link to="/session-templates" aria-current="page"><FontAwesomeIcon className="fas" icon={faTasks} />&nbsp;Session Templates</Link></li>
                        </ul>
                    </nav>

                    {/* Mobile Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
                        <ul>
                            <li class=""><Link to={`/dashboard`} aria-current="page"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back to Dashboard</Link></li>
                        </ul>
                    </nav>

                    {/* Modals */}
                    <div class={`modal ${selectedSessionTemplateForDeletion ? 'is-active' : ''}`}>
                        <div class="modal-background"></div>
                        <div class="modal-card">
                            <header class="modal-card-head">
                                <p class="modal-card-title">Are you sure?</p>
                                <button class="delete" aria-label="close" onClick={onDeselectSessionTemplateForDeletion}></button>
                            </header>
                            <section class="modal-card-body">
                                You are about to delete this session template and all the data inside of it. This action is cannot be undone. Are you sure you would like to continue?
                            </section>
                            <footer class="modal-card-foot">
                                <button class="button is-success" onClick={onDeleteConfirmButtonClick}>Confirm</button>
                                <button class="button" onClick={onDeselectSessionTemplateForDeletion}>Cancel</button>
                            </footer>
                        </div>
                    </div>

                    {/* Page */}
                    <nav class="box">
                        {/* Title + Options */}
                        <div class="columns">
                            <div class="column">
                                <h1 class="title is-4"><FontAwesomeIcon className="fas" icon={faTasks} />&nbsp;Session Templates</h1>
                            </div>
                            {!isFetching && <div class="column has-text-right">
                                <button class="button is-small is-info is-fullwidth-mobile" onClick={defaultFetchList}>
                                    <FontAwesomeIcon className="mdi" icon={faRefresh} /><span class="is-hidden-desktop is-hidden-tablet">&nbsp;Refresh</span>
                                </button>
                                &nbsp;
                                <button onClick={(e,i)=>{setShowFilter(!showFilter)}} class="button is-small is-link is-fullwidth-mobile" type="button">
                                    <FontAwesomeIcon className="mdi" icon={faFilter} />&nbsp;Filter
                                </button>
                                &nbsp;
                                <Link to={`/session-templates/add`} class="button is-small is-success is-fullwidth-mobile" type="button">
                                    <FontAwesomeIcon className="mdi" icon={faPlus} />&nbsp;New Session Template
                                </Link>
                            </div>}
                        </div>

                        {/* Filter Box */}
                        {showFilter &&
                            <div class="has-background-white-bis" style={{borderRadius:"15px", padding:"20px"}}>

                                {/* Filter Title + Clear Button */}
                                <div class="columns">
                                    <div class="column is-half">
                                        <strong><u><FontAwesomeIcon className="mdi" icon={faFilter} />&nbsp;Filter</u></strong>
                                    </div>
                                    <div class="column is-half has-text-right">
                                        <Link onClick={onClearFilterClick}><FontAwesomeIcon className="mdi" icon={faFilterCircleXmark} />&nbsp;Clear Filter</Link>
                                    </div>
                                </div>

                                {/* Filter Options */}
                                <div class="columns">
                                    <div class="column">
                                        <div class="">
                                            <FormSelectField
                                                label="Session Type"
                                                name="Session Type"
                                                placeholder="Text input"
                                                selectedValue={sessionType}
                                                options={SESSION_TEMPLATE_SESSION_TYPE_WITH_EMPTY_OPTIONS}
                                                errorText={errors && errors.sessionType}
                                                helpText=""
                                                onChange={(e)=>setSessionType(e.target.value)}
                                                maxWidth="150px"
                                            />
                                        </div>
                                    </div>
                                    <div class="column">
                                        <FormSelectField
                                            label="Sort by"
                                            name="sortByValue"
                                            placeholder="Pick sorting"
                                            selectedValue={sortByValue}
                                            helpText=""
                                            onChange={(e)=>setSortByValue(e.target.value)}
                                            options={SESSION_TEMPLATE_SORT_OPTIONS}
                                            isRequired={true}
                                        />
                                    </div>
                                </div>

                            </div>
                        }

                        {isFetching
                            ?
                            <>
                                <PageLoadingContent displayMessage={"Please wait"} />
                            </>
                            :
                            <>
                                <FormErrorBox errors={errors} />
                                {listData &&  (listData.length > 0 || previousCursors.length > 0)
                                    ?
                                    <div class="container">
                                        {/*
                                            ##################################################################
                                            EVERYTHING INSIDE HERE WILL ONLY BE DISPLAYED ON A DESKTOP SCREEN.
                                            ##################################################################
                                        */}
                                        <div class="is-hidden-touch" >
                                            <SessionTemplateListDesktop
                                                listData={listData}
                                                setPageSize={setPageSize}
                                                pageSize={pageSize}
                                                previousCursors={previousCursors}
                                                onPreviousClicked={onPreviousClicked}
                                                onNextClicked={onNextClicked}
                                                onSelectSessionTemplateForDeletion={onSelectSessionTemplateForDeletion}
                                            />
                                        </div>

                                        {/*
                                            ###########################################################################
                                            EVERYTHING INSIDE HERE WILL ONLY BE DISPLAYED ON A TABLET OR MOBILE SCREEN.
                                            ###########################################################################
                                        */}
                                        <div class="is-fullwidth is-hidden-desktop">
                                            <SessionTemplateListMobile
                                                listData={listData}
                                                setPageSize={setPageSize}
                                                pageSize={pageSize}
                                                previousCursors={previousCursors}
                                                onPreviousClicked={onPreviousClicked}
                                                onNextClicked={onNextClicked}
                                                onSelectSessionTemplateForDeletion={onSelectSessionTemplateForDeletion}
                                            />
                                        </div>
                                    </div>
                                    :
                                    <div class="hero-body">
                                      <p class="title">
                                          <FontAwesomeIcon className="fas" icon={faTable} />&nbsp;No data
                                      </p>
                                      <p class="subtitle">
                                          No data. <b><Link to="/session-templates/add">Click here&nbsp;<FontAwesomeIcon className="mdi" icon={faArrowRight} /></Link></b> to get started uploading your first video.
                                      </p>
                                    </div>
                                }
                            </>
                        }
                    </nav>
                </section>
            </div>
        </>
    );
}

export default SessionTemplateList;
